import { PDFDocument } from "pdf-lib";
import { delay } from "utils/tools";

interface IMergePdfResults {
  id: number;
  description: string;
  status: string;
}

export interface IMergePdfOptions {
  selectedPurchaseInvoices: IMergePdfResults[];
  provider: any;
  updateResults: (results: IMergePdfResults[]) => void;
  delayMs?: number; // delay between each merge (default: 500ms)
  revokeDelayMs?: number; // delay before revoking the blob URL (default: 60000ms)
}

/**
 * Fetches a PDF file from the given URL and returns it as an ArrayBuffer.
 *
 * @param url - The URL of the PDF file to fetch.
 * @param retries - Optional: number of retry attempts on failure (default is 2)
 * @param delayMs - Optional: delay between retries in milliseconds (default is 1000ms).
 * @returns A Promise that resolves to an ArrayBuffer containing the PDF data.
 * @throws An error if the fetch fails or the response is not OK.
 */
export const fetchPdfAsArrayBuffer = async (
  url: string,
  retries = 2,
  delayMs = 1000,
): Promise<ArrayBuffer> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(
        `Fetch failed with status: ${response.status} - ${response.statusText}`,
      );
    }

    return await response.arrayBuffer();
  } catch (error) {
    if (retries > 0) {
      await delay(delayMs);
      return fetchPdfAsArrayBuffer(url, retries - 1, delayMs);
    } else {
      throw new Error(`${error.message}`);
    }
  }
};

/**
 * Merges selected purchase invoice PDFs into a single PDF and opens it in a new tab.
 *
 * @param selectedPurchaseInvoices - Array of selected invoice objects to merge.
 * @param provider - Data provider used to fetch the invoice PDF URLs.
 * @param updateResults - Function to update UI with the current status of each invoice.
 * @param delayMs - Optional: delay between processing each invoice in milliseconds (default is 500ms).
 * @param revokeDelayMs - Optional: delay before revoking the created blob URL (default is 60,000ms).
 */
export const mergePurchaseInvoicePdfs = async ({
  selectedPurchaseInvoices,
  provider,
  updateResults,
  delayMs = 500,
  revokeDelayMs = 60000,
}: IMergePdfOptions): Promise<void> => {
  // Create a new PDF document that will hold the merged result
  const mergedPdf = await PDFDocument.create();

  // Loop through each selected invoice
  for (const selectedPurchaseInvoice of selectedPurchaseInvoices) {
    // Update the status to "Merging..." and trigger UI update
    selectedPurchaseInvoice.status = "Merging...";
    updateResults && updateResults([...selectedPurchaseInvoices]);

    try {
      // Fetch the invoice data including the PDF URL using the provided data provider
      const purchaseInvoice = await provider.getOne(
        "subcontractorPurchaseInvoices",
        {
          id: selectedPurchaseInvoice.id,
        },
      );

      // Fetch the actual PDF as an array buffer,
      // then load the PDF document using pdf-lib
      const pdfBytes = await fetchPdfAsArrayBuffer(
        purchaseInvoice?.data?.pdfUrl,
      );
      const pdf = await PDFDocument.load(pdfBytes);

      // Copy all pages from this PDF into the new merged PDF
      const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
      copiedPages.forEach((page) => mergedPdf.addPage(page));

      // Mark the invoice as successfully merged
      selectedPurchaseInvoice.status = "Merged";
    } catch (e) {
      // If any error occurs during fetch or processing, update the status with the error message
      selectedPurchaseInvoice.status = `Fail to merge PDF!, ${e.message}`;
    } finally {
      // Trigger UI update with new status
      updateResults && updateResults([...selectedPurchaseInvoices]);
    }

    // Wait for a specified delay before processing the next invoice
    await delay(delayMs);
  }

  // If at least one page was added to the merged PDF, continue to open the result
  if (mergedPdf.getPageCount() > 0) {
    // Serialize the PDF into a Uint8Array
    const mergedPdfBytes = await mergedPdf.save();

    // Create a blob from the byte array with MIME type application/pdf
    const blob = new Blob([mergedPdfBytes], { type: "application/pdf" });

    // Create a temporary object URL for the blob
    const blobUrl = URL.createObjectURL(blob);

    window.open(blobUrl, "_blank");

    // Schedule revocation of the blob URL after a delay to free up memory
    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
    }, revokeDelayMs);
  }
};
